<script setup>
//region Imports
import { ref, onMounted, reactive } from 'vue'
import StepHeader from '../components/StepHeader.vue'
//endregion
//region Methods
const readData = () => JSON.parse(localStorage.getItem('tc-claim-data'))

const readIssue = () => JSON.parse(localStorage.getItem('tc-claim-issue'))

const readSelectedItems = () => JSON.parse(localStorage.getItem('tc-claim-selected-items'))

const clickToBack = () => {
  switch (issue?.key) {
    case 'damage':
      location.replace('#/claim/damage')
      break
    default:
      location.replace(issue?.key ? `#/claim/${issue?.key}` : '#/claim/start')
      break
  }
}

const clickToCancel = () => {
  if (!confirm('Are you sure?')) {
    return
  }

  localStorage.removeItem('tc-claim-compensation')
  location.replace('#/order')
}

const clickToContinue = () => {
  if(! localStorage.getItem('tc-claim-compensation')) {
    alert('Please select a compensation before proceeding.')
    return
  }

  location.replace('#/claim/shipping')
}

const chooseOption = option => {
  selectedOption.value = option
  localStorage.setItem('tc-claim-compensation', JSON.stringify(option))
}
//endregion
//region Variables
const data = reactive(readData() ?? {})
const issue = reactive(readIssue() ?? {})
const items = reactive(readSelectedItems() ?? [])
let orderNumber = ref(data?.orderNumber)
const options = reactive([
  {
    key: 'store_credit',
    title: 'Store Credit',
    support: 'Sent directly to your email.',
    icon: '<svg fill=none height=31 viewBox="0 0 32 31" width=32 xmlns=http://www.w3.org/2000/svg><g id=icon><g id=Group><path d="M14.1583 9.96094H3.5773C2.44297 9.96094 1.52344 10.9079 1.52344 12.0759V27.501C1.52344 28.669 2.44297 29.6152 3.5773 29.6152H28.9509C30.0853 29.6152 31.0048 28.669 31.0048 27.501V12.0759C31.0048 10.9079 30.0853 9.96094 28.9509 9.96094H18.3699" id=Vector stroke=#2E1319 stroke-miterlimit=2 stroke-width=1.32705 /></g><g id=Group_2><path d="M31.0048 25.4102H1.52344" id=Vector_2 stroke=#2E1319 stroke-miterlimit=2 stroke-width=1.32705 /></g><g id=Group_3><path d="M31.0048 21.1953H1.52344" id=Vector_3 stroke=#2E1319 stroke-miterlimit=2 stroke-width=1.32705 /></g><g id=Group_4><path d="M11.3359 13.4727L16.2495 18.3862L21.1631 13.4727" id=Vector_4 stroke=#2E1319 stroke-miterlimit=2 stroke-width=1.32705 /></g><g id=Group_5><path d="M16.2656 18.3817V0" id=Vector_5 stroke=#2E1319 stroke-miterlimit=2 stroke-width=1.38619 /></g></g></svg>',
  },
  {
    key: 'replacement',
    title: 'Replace Item' + (items.length > 1 ? 's' : ''),
    support: 'Ships in 1-3 business days.',
    icon: '<svg fill=none height=34 viewBox="0 0 27 34" width=27 xmlns=http://www.w3.org/2000/svg><g id="Group 4292"><g id="Group 4291"><path d="M13.3984 4.79556C6.82521 4.79556 1.49655 10.1242 1.49655 16.6974C1.49655 19.6196 2.54964 22.2958 4.297 24.3672" id="Ellipse 162" stroke=#2E1319 stroke-width=1.40022 /><path d="M16.902 4.80044L14.1016 2V7.60089L16.902 4.80044Z" id="Vector 105" stroke=#2E1319 stroke-width=1.40022 /></g><g id="Group 4290"><path d="M14.0938 28.5794C20.667 28.5794 25.9956 23.2508 25.9956 16.6776C25.9956 13.7554 24.9426 11.0792 23.1952 9.00781" id="Ellipse 161" stroke=#2E1319 stroke-width=1.40022 /><path d="M11.2933 28.6097L14.0938 31.4102V25.8093L11.2933 28.6097Z" id="Vector 106" stroke=#2E1319 stroke-width=1.40022 /></g></g></svg>',
  },
])
let selectedOption = ref(null)
//endregion
//region Lifecycle
onMounted(() => {
  if (!orderNumber.value) {
    location.replace('/')
  }

  selectedOption.value = JSON.parse(localStorage.getItem('tc-claim-compensation'))
})
//endregion
</script>

<template>

<!--
<figure class="w-full h-[305px] overflow-hidden bg-no-repeat bg-right bg-cover"
          style="background-image: url(https://cdn.shopify.com/s/files/1/0067/9532/9609/files/DSC01071.png?v=1713213218);">&nbsp;</figure>
-->

  <div class="px-[20px] my-[30px] lg:px-[40px]">

    <step-header :data="data" :items="items"/>

    <main class="lg:max-w-[420px] mx-auto">

      <div class="mx-auto flex flex-row mb-[30px] items-center">
        <button type="button" @click="clickToBack">
          <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
            <path d="M8 7L2 13L8 19" stroke="black" stroke-width="1.5"/>
          </svg>
        </button>
        <h1 class="text-tc-subtitle font-bold font-minionpro tracking-wider text-tc-black" v-text="`Resolution Center`"></h1>
      </div>

      <h2 class="text-tc-xl text-tc-gray mb-[30px] tracking-wider font-normal font-avenir" v-text="`How would you like to resolve this issue?`"></h2>

      <ul class="grid grid-cols-2 gap-[20px]">
        <li v-for="option in options" :key="option.key" class="flex flex-col gap-[11px] bg-tc-off-white border p-[20px]" @click="chooseOption(option)"
            :class="{ 'border-black': selectedOption?.key === option.key, 'border-tc-off-white': selectedOption?.key !== option.key }">
          <div class="w-10 h-10" v-html="option.icon"></div>
          <h3 class="text-tc-xl text-tc-maroon font-medium font-avenir tracking-wider" v-text="option.title"></h3>
          <p class="text-tc-lg text-tc-muted-brown tracking-wider font-avenir font-normal max-w-[90%]" v-text="option.support"></p>
        </li>
      </ul>

      <p class="mt-[20px] leading-relaxed text-tc-xl text-black tracking-wider font-normal font-avenir"
      >Please note if issue is approved, your preference is dependant on availability of
        items and the discretion of Tres Colori.</p>

      <div class="my-[30px] flex flex-row justify-between">
        <button type="button"
            @click="clickToCancel"
            class="text-tc-xl text-black tracking-wider font-avenir font-normal py-2.5 px-8 border-b border-tc-creme"
        >Cancel
        </button>
        <button type="submit"
            @click="clickToContinue"
            class="text-tc-xl text-black tracking-wider font-avenir font-normal py-2.5 px-8 border border-tc-creme"
        >Next
        </button>
      </div>

    </main>

  </div>

</template>

<style scoped>
</style>